import { IPlanKey } from '../types';

import { getDateByNumberOfNextDays } from '~/utils/dateTime';
import { usePricingPlanData } from './usePricingPlanData';
import { CONFIG_PRICING } from '../configs';
import { usePricingStore } from '../stores';
import { useMemo } from 'react';

export interface IPricingPlanTrialActive {
  isTrial: boolean;
  /**
   * @returns IPlanKey.TRIAL_FUNNEL_OPT | IPlanKey.TRIAL_PAGE_OPT | IPlanKey.TRIAL_PROFIT_OPT
   */
  trialPlanKey?: IPlanKey;
  trialPlanName?: string;
  trialEndsAt: Date;
  totalTrialDays: number;
  usedTrialDays: number;
  remainingTrialDay: number;
  isLastDayOfTrial: boolean;
  isTrialExpired: boolean;
  isLast2DaysOfTrial: boolean;

  isTrialFunnelOpt: boolean;
  isTrialPageOpt: boolean;
  isTrialProfitOpt: boolean;
}

export const usePricingPlanTrialActive = (): IPricingPlanTrialActive => {
  const subscriptionTrialUsages = usePricingStore((state) => state.subscriptionTrialUsages);
  const shopPlatformPlanActive = usePricingStore((state) => state.shopPlatformPlanActive);

  const { getPlanKeyFromId, getPlanNameByPlanKey } = usePricingPlanData();

  const planTrial = useMemo(() => {
    const planActive = shopPlatformPlanActive?.plan;
    if (!planActive?.isTrial) return;

    return subscriptionTrialUsages?.find((item) => item.planID === planActive.id);
  }, [shopPlatformPlanActive?.plan, subscriptionTrialUsages]);

  const isTrial = !!planTrial;
  const trialPlanKey = getPlanKeyFromId(planTrial?.planID ?? '');
  const trialPlanName = getPlanNameByPlanKey(trialPlanKey);
  const totalTrialDays = planTrial?.trialDays || 0;
  const usedTrialDays = planTrial?.usedTrialDays || 0;
  const remainingTrialDay = Math.max(0, totalTrialDays - usedTrialDays);
  const trialEndsAt = getDateByNumberOfNextDays(remainingTrialDay);

  const isLastDayOfTrial = remainingTrialDay === 1;
  const isTrialExpired = remainingTrialDay <= 0;
  const isLast2DaysOfTrial =
    !isTrialExpired && remainingTrialDay <= CONFIG_PRICING.TRIAL_DAYS_LEFT_TO_SHOW_NOTICE;
  const isTrialFunnelOpt = isTrial && trialPlanKey === IPlanKey.TRIAL_FUNNEL_OPT;
  const isTrialPageOpt = isTrial && trialPlanKey === IPlanKey.TRIAL_PAGE_OPT;
  const isTrialProfitOpt = isTrial && trialPlanKey === IPlanKey.TRIAL_PROFIT_OPT;

  return {
    isTrial,
    trialEndsAt,
    trialPlanKey,
    trialPlanName,
    remainingTrialDay,
    isLastDayOfTrial,
    totalTrialDays,
    usedTrialDays,
    isTrialExpired,
    isLast2DaysOfTrial,
    isTrialFunnelOpt,
    isTrialPageOpt,
    isTrialProfitOpt,
  };
};
